import React, { useCallback, useEffect, useState } from 'react';
import { Empty } from 'antd';
import type { Park } from '../../types';
import type { DockingRecord } from '../../types/docking';
import { DockingRecordCard } from './DockingRecordCard';
import { DockingRecordForm } from './DockingRecordForm';

export interface DockingDetailPanelProps {
  parks: Park[];
  selectedParkId: string | null;
  selectedParkRecords: DockingRecord[];
  onSave: (record: DockingRecord) => void;
  onDelete: (id: string) => void;
}

type Mode = { type: 'view' } | { type: 'create'; parkId?: string } | { type: 'edit'; record: DockingRecord };

export function DockingDetailPanel(props: DockingDetailPanelProps): React.ReactElement {
  const { parks, selectedParkId, selectedParkRecords, onSave, onDelete } = props;
  const [mode, setMode] = useState<Mode>({ type: 'view' });

  useEffect(() => {
    setMode({ type: 'view' });
  }, [selectedParkId]);

  const parkName = selectedParkId
    ? parks.find((p) => p.id === selectedParkId)?.name
    : null;

  const handleSave = useCallback(
    (record: DockingRecord) => {
      onSave(record);
      setMode({ type: 'view' });
    },
    [onSave],
  );

  const handleCancel = useCallback(() => setMode({ type: 'view' }), []);

  if (mode.type === 'create') {
    return (
      <aside className="dash-panel dash-panel--right">
        <DockingRecordForm
          parks={parks}
          fixedParkId={mode.parkId || undefined}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </aside>
    );
  }

  if (mode.type === 'edit') {
    return (
      <aside className="dash-panel dash-panel--right">
        <DockingRecordForm
          parks={parks}
          initialRecord={mode.record}
          fixedParkId={mode.record.parkId}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </aside>
    );
  }

  if (!selectedParkId || selectedParkRecords.length === 0) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="dash-empty-text">
                {selectedParkId ? '该园区暂无对接记录' : '请在左侧选择园区查看对接记录'}
              </span>
            }
          />
        </div>
      </aside>
    );
  }

  return (
    <aside className="dash-panel dash-panel--right">
      <div className="dash-detail-title">
        {parkName ?? '园区'}的对接记录（{selectedParkRecords.length} 条）
      </div>
      <div className="docking-records-list">
        {selectedParkRecords.map((r) => (
          <DockingRecordCard
            key={r.id}
            record={r}
            onEdit={(rec) => setMode({ type: 'edit', record: rec })}
            onDelete={onDelete}
          />
        ))}
      </div>
    </aside>
  );
}
